import React, {useEffect, useState} from 'react';
import { Link } from 'react-router';
import './login.scss';

import TextInput from '../Form/TextInput/TextInput';
import Button, {LoadingButton} from '../Form/Button/Button';
import chevLeft from '../../assets/images/chev-left-royal.svg';
import ChangePasswordWithConfirmationInput from '../Form/PasswordInput/ChangePasswordWithConfirmationInput';
import VerifyOtp from './VerifyOtp';
import { useMutation } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import {useLogger} from "../../contexts/Logger";
import {emailRegex} from "../../utils/helpers";
import withFeatureFlags from "../FeatureFlags/withFeatureFlags";
import './TroubleLoggingIn.scss';
import bottomLeftCornerImg from "../../assets/images/Stripes Light Bottom Left.svg"
import TopRightCornerImg from "../../assets/images/Stripes Light Top Right.svg"
import CommonContainerStrips from "./CommonContainerStrips";
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";
/** @import {WithFeatureFlagsProps} from '../FeatureFlags/FeatureFlagsTypes' */
/** @typedef {WithFeatureFlagsProps & {pageTitle?: string}} ForgotPasswordProps */

const RESET_PASSWORD_PATH = '/reset-password';
const PASSWORD_RESET_FEATURE_NAME = 'Password Reset';
const PASSWORD_RESET_ENTRY_EVENT = 'Investec-Save-secure|page_load|reset-password-journey';
const PASSWORD_UPDATE_EVENT_PREFIX = 'Investec-Save-secure|Intergration Event|';

const ForgotPassword = (/** @type {ForgotPasswordProps} */ props) => {
  const pageTitle = props.pageTitle || 'Forgot your password?';
  const analytics = useUnifiedAnalytics();

  const SEND_FORGOT_PASSWORD_OTP = gql`
    mutation SendOtpByEmail($request : SendOtpByEmailType!){
      sendOtpByEmail(request:$request) {
        success
        verificationToken
      }
    }  
  `;

  const UPDATE_PASSWORD = gql`
    mutation ResetPassword($request : ChangePasswordRequestType!){
      resetPassword(request:$request) {
        success
      }
    }  
  `;

  const UPDATE_PASSWORD_METADATA = gql`
    mutation ResetPasswordMeta($request : ChangePasswordMetaRequestType!){
      resetPasswordMeta(request:$request) {
        firstName
        lastName
      }
    }  
  `;

  const logger = useLogger();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [stepOneLoading, setStepOneLoading] = useState(false);
  const [stepTwoLoading, setStepTwoLoading] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [_showPasswordError, setShowPasswordError] = useState(false);
  const [mobileNumber, _setMobileNumber] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [emailBlurValid, setEmailBlurValid] = useState(true);

  const [sendForgotPassword] = useMutation(SEND_FORGOT_PASSWORD_OTP);
  const [updatePassword] = useMutation(UPDATE_PASSWORD);
  const [updatePasswordMeta] = useMutation(UPDATE_PASSWORD_METADATA);

  const getJourneyName = () => window.location.pathname === RESET_PASSWORD_PATH ? 'reset-password' : 'forgot-password';

  const trackPasswordUpdateResult = (/** @type {'success' | 'failure'} */ result) => {
    analytics.trackAnalyticsIntegrationEvent(
        `${PASSWORD_UPDATE_EVENT_PREFIX}${getJourneyName()}-${result}`,
        PASSWORD_RESET_FEATURE_NAME,
        result,
        'digital savings',
        getJourneyName()
    );
  };

  useEffect(() => {
    if (getJourneyName() === 'reset-password') {
      analytics.trackAnalyticsPageEvent(
          'reset password journey load',
          {},
          PASSWORD_RESET_FEATURE_NAME,
          PASSWORD_RESET_ENTRY_EVENT
      );
    }
  }, []);

  const setEmailChange = (/** @type {string} */ email) =>  {
    setShowEmailError(false);
    setEmailBlurValid(true);
    setEmail(email);
  }

  const emailValid = () => {
    return email && emailRegex.test(email);;
  };

  const setPasswordChange = (/** @type {string | null} */ password) => {
    // validate
    setPassword(password || '');
  }

  const renderNextButton = (/** @type {string} */ label, /** @type {boolean} */ isLoading, /** @type {boolean | undefined} */ disabled, /** @type {(() => void) | undefined} */ callback) => {
    return isLoading ?
        <LoadingButton customClassNames={['loginButton', 'button--primary']} /> :
        <Button customClassNames={['loginButton', 'button--primary']} disabled={disabled} label={label} onClick={callback} />
  };

  const stepOneNextClicked =  async (/** @type {React.MouseEvent<HTMLLabelElement, MouseEvent>} */ e) => {
    e.preventDefault();

    setStepOneLoading(true);

    try {
      let result = await sendForgotPassword({
        variables: {
          request: {
            email: email
          }
        }
      })

      setStepOneLoading(false);

      if (result.data.sendOtpByEmail.success) {
        setVerificationToken(result.data.sendOtpByEmail.verificationToken);
        setStep(2);
      }
      else {
        setStep(2);
      }
    }
    catch {
      setStepOneLoading(false);
      setStep(2);
    }
  };

  const otpVerifiedSuccess = () => {
    setStep(3);
    updatePasswordMetaClick();
  };

  const validateBlur = () => {
    if (!emailValid()) {
      setEmailBlurValid(false);
    }
    else {
      setEmailBlurValid(true);
    }
  }

  const renderStepOne = () => {
    return (
        <form>
          <div className="login__line">
            Please enter your email address that you use to login.
          </div>

          <label className="lblEmaildadadsAddress">Your email address</label>

          <TextInput className="login__input" type="text" placeholder={""} value={email} onChange={setEmailChange} onBlur={validateBlur} />
          <div className={!emailBlurValid ? "login__error" : "login__error login__error--hide"}>
            <span className="login__error--wrap">That doesn't look right, please try again</span>
          </div>
          <div className={showEmailError ? "login__error" : "login__error login__error--hide"}>
            <span className="login__error--wrap">Looks like you've entered the wrong information, please try again or select 'forgot email?' from the login page.</span>
          </div>
          {renderNextButton("Next", stepOneLoading, !emailValid(), stepOneNextClicked)}
        </form>
    );
  };

  const renderStepTwo = () => {
    return <VerifyOtp
        noNumberMessage={true}
        mobileNumber={mobileNumber}
        verificationToken={verificationToken}
        onSuccess={otpVerifiedSuccess} />;
  }

  const updatePasswordMetaClick = async () => {
    setStepTwoLoading(true);

    try {
      let result = await updatePasswordMeta({
        variables: {
          request: {
            verificationToken: verificationToken,
            email: email
          }
        }
      })

      if (result.data.resetPasswordMeta.firstName) {
        setFirstName(result.data.resetPasswordMeta.firstName);
        setLastName(result.data.resetPasswordMeta.lastName);
      }
      else {
        setFirstName('');
        setLastName('');
      }

      setStepTwoLoading(false);
    }
    catch {
      setStepTwoLoading(false);
      setShowPasswordError(true);
    }
  };

  const updatePasswordClick = async (/** @type {React.MouseEvent<HTMLLabelElement, MouseEvent>} */ e) => {
    e.preventDefault();

    setStepTwoLoading(true);

    try {
      logger.info('Updating password');
      let result = await updatePassword({
        variables: {
          request: {
            verificationToken: verificationToken,
            password: password,
          }
        }
      })

      if (result.data.resetPassword.success) {
        trackPasswordUpdateResult('success');
        window.location.href = '/login';
      }
      else {
        trackPasswordUpdateResult('failure');
        setStepTwoLoading(false);
        setShowPasswordError(true);
      }
    }
    catch {
      trackPasswordUpdateResult('failure');
      setStepTwoLoading(false);
      setShowPasswordError(true);
    }
  };

  const renderStepThree = () => {
    let userProfile = {
      email: email,
      firstName: firstName,
      lastName: lastName
    };

    return (
        <form>
          <div className="login__line">
            Enter the new password you would like to use to login.
          </div>
          <div className="login__margin">
            <ChangePasswordWithConfirmationInput
                profile={userProfile}
                className="login__input"
                onChange={setPasswordChange}
                alwaysShowValidations
            />
          </div>
          {renderNextButton("Confirm", stepTwoLoading, password === null || password === '', updatePasswordClick)}
        </form>
    );
  }

  const renderStep = () => {
    switch (step) {
      default: return renderStepOne();
      case 2: return renderStepTwo();
      case 3: return renderStepThree();
    }
  }
  const [isTabletOrAbove, setIsTabletOrAbove] = useState(window.innerWidth >= 768);

  return (
      <>
        <CommonContainerStrips propData=
                                   {{
                                     bottomLeftImg: bottomLeftCornerImg, topRightImg: TopRightCornerImg, children:
                                         (
                                             <div className="troubleLoggingIn__frame3423">
                                               <div className="troubleLoggingIn__login2">
                                                 <Link to="/login" className="login__link login__link--back loginP3__backButton"><img
                                                     src={chevLeft} />Back
                                                   to login</Link>
                                                 <h1 className="loginP3__title" style={{ width: 'auto' }}>{pageTitle}</h1>
                                                 {renderStep()}
                                               </div>
                                             </div>
                                         )
                                   }}
        />
      </>
  );
}

export default withFeatureFlags(ForgotPassword);
